import { Fragment } from "react";
import PageSectionFrame from "./PageSectionFrame";

// The sections of a Custom booking page, read from professional.pageSections:
// an ordered list of { id, type, visible, content }. Only a Custom page reads
// the list; a Standard page always draws header, gallery, services.
//
// Reading is forgiving, like the server's: a missing, empty or unreadable list
// is the default list, which draws the same page as Standard.

export const DEFAULT_PAGE_SECTIONS = Object.freeze([
  Object.freeze({ id: "header", type: "header", visible: true }),
  Object.freeze({ id: "gallery", type: "gallery", visible: true }),
  Object.freeze({ id: "services", type: "services", visible: true }),
]);

const isRecord = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

export const resolvePageSections = (value) => {
  const sections = (Array.isArray(value) ? value : []).filter(
    (section) =>
      isRecord(section) &&
      typeof section.type === "string" &&
      section.type.trim() !== "",
  );
  return sections.length > 0 ? sections : DEFAULT_PAGE_SECTIONS;
};

export const isCustomProfileFormat = (professional) =>
  professional?.profileFormat === "custom";

// Draws each visible section with the renderer for its type, in list order.
// A type with no renderer draws nothing, and so does a section with nothing
// in it: each renderer returns null when it's empty.
//
// `frame` is the visual editor's:
// { titles, selectedId, movingId, peekId, onSelect, onPeek }.
// With it each section gets a name tab; without it the markup is exactly the
// page's, which is all a client ever gets.
export const renderPageSections = (sections, renderers, frame = null) =>
  sections.map((section, index) => {
    if (section.visible === false) return null;
    const render = renderers[section.type];
    if (typeof render !== "function") return null;
    const key =
      typeof section.id === "string" && section.id
        ? section.id
        : `${section.type}-${index}`;
    if (!frame) return <Fragment key={key}>{render(section)}</Fragment>;
    return (
      <PageSectionFrame
        key={key}
        section={section}
        title={frame.titles?.[section.id]}
        selected={Boolean(frame.selectedId) && frame.selectedId === section.id}
        moving={Boolean(frame.movingId) && frame.movingId === section.id}
        peeked={Boolean(frame.peekId) && frame.peekId === section.id}
        onSelect={frame.onSelect}
        onPeek={frame.onPeek}
      >
        {render(section)}
      </PageSectionFrame>
    );
  });
